import { Text, Badge } from '@radix-ui/themes';

import {
  FingerprintValue,
  ConditionOperator,
  FingerprintValueType,
} from '../../types';
import { MonetaryValue } from '../../lib/workflow-defs';

import { MonetaryValueDisplay, HashShortener } from '@/components';
import { useGetListsQuery } from '../../graphql/queries/GetLists.generated';

interface ConditionDisplayProps {
  operation: ConditionOperator;
  value: FingerprintValue | string | undefined;
}

const operatorMap: Record<ConditionOperator, string> = {
  [ConditionOperator.Equals]: '=',
  [ConditionOperator.LessThan]: '<',
  [ConditionOperator.GreaterThan]: '>',
  [ConditionOperator.LessThanOrEqual]: '≤',
  [ConditionOperator.GreaterThanOrEqual]: '≥',
  [ConditionOperator.Between]: 'between',
  [ConditionOperator.Contains]: 'contains',
  [ConditionOperator.EndsWith]: 'ends with',
  [ConditionOperator.IsNotNull]: 'is not null',
  [ConditionOperator.IsNull]: 'is null',
  [ConditionOperator.NotBetween]: 'not between',
  [ConditionOperator.NotContains]: 'not contains',
  [ConditionOperator.NotEquals]: '!=',
  [ConditionOperator.StartsWith]: 'starts with',
  [ConditionOperator.InList]: 'in',
  [ConditionOperator.NotInList]: 'not in',
};

const valueComponents: Record<
  FingerprintValueType,
  React.ComponentType<{ value: any }>
> = {
  [FingerprintValueType.MonetaryValue]: ({ value }: { value: any }) => (
    <MonetaryValueDisplay value={value as MonetaryValue} size="sm" />
  ),
  [FingerprintValueType.Hash]: ({ value }: { value: any }) => (
    <HashShortener hash={value as string} />
  ),
  [FingerprintValueType.String]: ({ value }: { value: any }) => (
    <Text size="1" weight="medium">
      {value}
    </Text>
  ),
  [FingerprintValueType.Boolean]: ({ value }: { value: any }) => (
    <Text size="1" weight="medium">
      {value}
    </Text>
  ),
  [FingerprintValueType.Null]: ({ value }: { value: any }) => (
    <Text size="1" weight="medium">
      {value}
    </Text>
  ),
  [FingerprintValueType.Number]: ({ value }: { value: any }) => (
    <Text size="1" weight="medium">
      {value}
    </Text>
  ),
};

// Helper to check if operation is list-based
const isListOperation = (op: ConditionOperator): boolean =>
  op === ConditionOperator.InList || op === ConditionOperator.NotInList;

// Component to display list name for list operations
const ListValueDisplay = ({ listId }: { listId: string }) => {
  const { data, loading } = useGetListsQuery({ variables: { first: 100 } });

  if (loading) {
    return (
      <Text size="1" color="gray">
        Loading...
      </Text>
    );
  }

  const list = data?.lists?.edges?.find((edge) => edge.node.id === listId);
  const listName = list?.node.name || 'Unknown List';

  return (
    <Badge variant="surface" color={list?.node.isSystem ? 'red' : 'blue'}>
      {listName}
    </Badge>
  );
};

const ConditionDisplay = ({ operation, value }: ConditionDisplayProps) => {
  if (!value)
    return (
      <Text size="1" color="gray">
        Empty
      </Text>
    );

  // Handle list operations specially - the value is a list UUID
  if (isListOperation(operation)) {
    const listId =
      typeof value === 'string' ? value : (value as FingerprintValue)?.value;
    return (
      <>
        <Text
          size="1"
          weight="medium"
          color="gray"
          style={{ fontFamily: 'monospace' }}
        >
          {operatorMap[operation]}
        </Text>
        <ListValueDisplay listId={listId as string} />
      </>
    );
  }

  const ValueComponent = valueComponents[(value as FingerprintValue).valueType];

  return (
    <>
      <Text
        size="1"
        weight="medium"
        color="gray"
        style={{ fontFamily: 'monospace' }}
      >
        {operatorMap[operation]}
      </Text>
      <ValueComponent value={(value as FingerprintValue).value} />
    </>
  );
};

export default ConditionDisplay;
